<script setup lang="ts">
import Mapbox from 'mapbox-gl';

import { MAPBOX_PROVIDE_INJECT, IAD_PARIS, MARKER_COLOR, useSafePosition } from '~/composables/mapbox/map';
import type { LgnLat, MapBoxInjected, UnsafeLgnLat } from '~/composables/mapbox/map';

const props = withDefaults(
  defineProps<{
    lgnLat?: UnsafeLgnLat
    draggable?: boolean
  }>(),
  {
    lgnLat: () => IAD_PARIS,
    draggable: false,
  },
);

const emit = defineEmits<{
  'update:lgnLat': [lgnLat: LgnLat]
}>();

const marker = shallowRef<mapboxgl.Marker>();
const { position } = useSafePosition(toRef(props, `lgnLat`));

const { mapbox } = inject(MAPBOX_PROVIDE_INJECT) as MapBoxInjected;

watch(mapbox, () => {
  if (!mapbox.value) return;
  // https://docs.mapbox.com/mapbox-gl-js/api/markers/#marker
  marker.value = new Mapbox.Marker({ draggable: props.draggable, color: MARKER_COLOR });
  marker.value.setLngLat(position.value);
  marker.value.addTo(mapbox.value);
  marker.value.on(`dragend`, onDragend);
  // NOTE: Immediate to been trigger during tests
  // Without this
  // • since “provide” will be injected before “mount’
  // • new Mapbox.Marker will never been called
}, { immediate: true });

onBeforeUnmount(() => {
  if (!mapbox.value) return;
  marker.value?.remove();
});

watch(position, () => {
  if (!marker.value) return;
  marker.value.setLngLat(position.value);
});
watch(() => props.draggable, () => {
  if (!marker.value) return;
  marker.value.setDraggable(props.draggable);
});

function onDragend() {
  if (!props.draggable) return;
  if (!marker.value) return;
  const lngLat = marker.value.getLngLat();
  emit(`update:lgnLat`, [lngLat.lng, lngLat.lat]);
}

// Template can't be empty. Needed to avoid Vue warning without rendering anything
// “Avoid app logic that relies on enumerating keys on a component instance. The keys will be empty in production mode to avoid performance overhead.”
const render = () => null;
</script>

<template>
  <render />
</template>
